<template>
    <div>
        <v-app-bar app flat color="#f1f2f3">
            <v-container fluid>
                <v-row>
                    <v-col cols="12" sm="4" class="d-flex align-center">
                        <v-autocomplete
                            outlined
                            :items="$isError(resourcePools) ? [] : resourcePools"
                            :loading="fetching.resourcePools || fetchingAllCharts"
                            :disabled="fetching.resourcePools || fetchingAllCharts"
                            :error="$isError(resourcePools)"
                            :error-messages="getErrorMsg(resourcePools)"
                            v-model="selectedResourcePoolId"
                            item-text="long_id"
                            item-value="rpid"
                            dense
                            label="Resource pool"
                            placeholder="Type to search"
                            hide-details="auto"
                            :menu-props="{ closeOnContentClick: true }">
                            <template v-slot:item="data">
                                <v-list-item :to="selectLink('rp', { rpid: data.item.rpid })">
                                    <v-list-item-content :class="data.item.deleted && 'deleted'">{{ data.item.long_id }}</v-list-item-content>
                                    <v-tooltip top>
                                        <template v-slot:activator="{ on, attrs }">
                                            <v-icon small color="grey" class="ml-3" v-bind="attrs" v-on="on">
                                                {{ resourcePoolRole(data.item.is_rp_manager).icon }}
                                            </v-icon>
                                        </template>
                                        <span>{{ resourcePoolRole(data.item.is_rp_manager).label }}</span>
                                    </v-tooltip>
                                </v-list-item>
                            </template>
                        </v-autocomplete>
                        <contacts-menu level="rp" :rpId="selectedResourcePoolId" :isRPManager="isRPManager"></contacts-menu>
                        <div class="pl-6" :class="{ hidden: !showOrgPools }">
                            <v-icon>keyboard_arrow_right</v-icon>
                        </div>
                    </v-col>
                    <v-col cols="12" sm="4" class="d-flex align-center">
                        <v-autocomplete
                            v-if="showOrgPools"
                            outlined
                            :items="$isError(orgPools) ? [] : orgPools"
                            :loading="fetching.resourcePools || fetching.orgPools || fetchingAllCharts"
                            :disabled="fetching.resourcePools || fetching.orgPools || fetchingAllCharts"
                            :error="$isError(orgPools)"
                            :error-messages="getErrorMsg(orgPools)"
                            v-model="selectedOrgId"
                            item-text="long_id"
                            item-value="oid"
                            dense
                            label="Organization"
                            placeholder="Type to search"
                            hide-details="auto"
                            clearable
                            @click:clear="clearOrg()"
                            :menu-props="{ closeOnContentClick: true }">
                            <template v-slot:item="data">
                                <v-list-item :to="selectLink('oid', { rpid: $route.params.rpid, oid: data.item.oid })">
                                    <v-list-item-content :class="data.item.deleted && 'deleted'">
                                        {{ data.item.long_id }}
                                    </v-list-item-content>
                                </v-list-item>
                            </template>
                        </v-autocomplete>
                        <contacts-menu v-if="showOrgPools" level="org" :rpId="Number($route.params.rpid)" :orgId="Number($route.params.oid)"></contacts-menu>
                        <div class="pl-6" :class="{ hidden: !showSpacePools }">
                            <v-icon>keyboard_arrow_right</v-icon>
                        </div>
                    </v-col>
                    <v-col cols="12" sm="4" class="d-flex align-center justify-end">
                        <v-autocomplete
                            v-if="showSpacePools"
                            outlined
                            :items="$isError(spacePools) ? [] : spacePools"
                            :loading="fetching.resourcePools || fetching.orgPools || fetching.spacePools || fetchingAllCharts"
                            :disabled="fetching.resourcePools || fetching.orgPools || fetching.spacePools || fetchingAllCharts"
                            :error="$isError(spacePools)"
                            :error-messages="getErrorMsg(spacePools)"
                            v-model="selectedSpaceId"
                            item-text="long_id"
                            item-value="sid"
                            dense
                            label="Space"
                            placeholder="Type to search"
                            hide-details="auto"
                            clearable
                            @click:clear="clearSpace()"
                            :menu-props="{ closeOnContentClick: true }">
                            <template v-slot:item="data">
                                <v-list-item :to="selectLink('sid', { rpid: $route.params.rpid, oid: $route.params.oid, sid: data.item.sid })">
                                    <v-list-item-content :class="data.item.deleted && 'deleted'">
                                        {{ data.item.long_id }}
                                    </v-list-item-content>
                                </v-list-item>
                            </template>
                        </v-autocomplete>
                        <contacts-menu
                            v-if="showSpacePools"
                            level="space"
                            :rpId="Number($route.params.rpid)"
                            :spaceId="Number($route.params.sid)"></contacts-menu>
                    </v-col>
                </v-row>
            </v-container>
        </v-app-bar>

        <v-container>
            <v-row>
                <v-col>
                    <v-card class="mt-3 overflow-hidden">
                        <v-tabs ref="tabs" color="primary">
                            <v-tab v-for="(tab, index) in visibleTabs" :key="`tab-${index}`" :to="tabLink(tab.to)">
                                <v-icon small left>{{ tab.icon }}</v-icon>
                                {{ tab.label }}
                            </v-tab>
                        </v-tabs>
                    </v-card>
                    <router-view @fetching-charts="fetchingCharts"></router-view>
                </v-col>
            </v-row>
        </v-container>
    </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import { find } from 'lodash'
import { organizationRole } from '@/utils'

const ContactsMenu = () => import('../components/ContactsMenu.vue')

export default {
    name: 'MonitoringDashboard',
    components: { ContactsMenu },
    data() {
        return {
            resourcePools: [],
            orgPools: [],
            spacePools: [],
            fetching: {
                resourcePools: true,
                orgPools: true,
                spacePools: true
            },
            selectedResourcePoolId: null,
            selectedOrgId: null,
            selectedSpaceId: null,
            tab: null,
            tabs: [
                { icon: 'line_axis', label: 'Overview', to: 'overview' },
                { icon: 'data_usage', label: 'Plan utilization', visible: 'isRPManager', to: 'plan-utilization' },
                { icon: 'savings', label: 'Credit utilization', to: 'credit-utilization' },
                { icon: 'checklist', label: 'Inventory', visible: 'isRPManager', to: 'inventory' }
            ],
            errorMessage: 'Failed to fetch resource list!',
            fetchingAllCharts: false
        }
    },
    methods: {
        async getOrgList() {
            if (!this.$route.params.rpid) return
            this.fetching.orgPools = true
            const { data } = await this.$axios.get(`/resource/rp_tree/rpid/${this.$route.params.rpid}`)
            this.orgPools = data
            this.fetching.orgPools = false
        },
        async getSpaceList() {
            if (!this.$route.params.rpid || !this.$route.params.oid) return
            this.fetching.spacePools = true
            const { data } = await this.$axios.get(`/resource/rp_tree/rpid/${this.$route.params.rpid}/oid/${this.$route.params.oid}`)
            this.spacePools = data
            this.fetching.spacePools = false
        },
        async getResourceList() {
            // fetch rps
            this.fetching.resourcePools = true
            const { data } = await this.$axios.get('/resource/rp_tree')
            this.resourcePools = data
            this.fetching.resourcePools = false
        },
        getErrorMsg(target) {
            return this.$isError(target) ? this.errorMessage : ''
        },
        resourcePoolRole(isManager) {
            return organizationRole(isManager ? 'manager' : 'member')
        },

        tabLink(tabTo) {
            let level = ''
            if (this.$route.name.includes('-rp')) {
                level = 'rp'
            }
            if (this.$route.name.includes('-oid')) {
                level = 'oid'
            }
            if (this.$route.name.includes('-sid')) {
                level = 'sid'
            }
            return {
                name: `reporting-${level}-${tabTo}`,
                params: { rpid: this.$route.params.rpid, startDate: this.$route.params.startDate, endDate: this.$route.params.endDate }
            }
        },
        selectLink(level, params) {
            params = { ...params, startDate: this.$route.params.startDate, endDate: this.$route.params.endDate }
            return { name: `reporting-${level}-${this.currentTab}`, params }
        },
        clearOrg() {
            this.$router.push({
                name: `reporting-rp-${this.currentTab}`,
                params: { rpid: this.$route.params.rpid, startDate: this.$route.params.startDate, endDate: this.$route.params.endDate }
            })
        },
        clearSpace() {
            this.$router.push({
                name: `reporting-rp-${this.currentTab}`,
                params: {
                    rpid: this.$route.params.rpid,
                    oid: this.$route.params.oid,
                    startDate: this.$route.params.startDate,
                    endDate: this.$route.params.endDate
                }
            })
        },
        fetchingCharts(fetching) {
            this.fetchingAllCharts = fetching
        }
    },
    computed: {
        ...mapState('userStore', ['isAccountManager']),
        ...mapGetters(['isSysadmin']),
        showOrgPools() {
            return !this.$route.name.includes('overview')
        },
        showSpacePools() {
            return this.selectedOrgId && !this.$route.name.includes('overview') && !this.$route.name.includes('inventory')
        },
        visibleTabs() {
            return this.tabs.filter(tab => {
                if (!tab.visible) return true
                return this[tab.visible]
            })
        },
        isRPManager() {
            const isRpManager = this.selectedRP?.is_rp_manager
            this.$store.dispatch('userStore/setRpManager', isRpManager)
            return isRpManager
        },
        selectedRP() {
            const selectedRp = find(this.resourcePools, { rpid: Number(this.$route.params.rpid) })
            this.$store.dispatch('userStore/setSelectedRP', selectedRp)
            return selectedRp
        },
        currentTab() {
            const tabName = this.$route.name.split('-').slice(2).join('-')
            return tabName
        }
    },
    watch: {
        $route: {
            async handler(to) {
                await this.getResourceList()

                const managedPools = this.resourcePools.filter(rp => rp.is_rp_manager)
                this.$store.dispatch('userStore/setManagedResourcePools', managedPools)

                // ENTRY POINT: Reload with first resource pool selected if no rpid is provided
                if (to.name === 'reporting-dashboard') {
                    this.$router.push({ name: 'reporting-rp-overview', params: { rpid: this.resourcePools[0].rpid } })
                }

                this.selectedResourcePoolId = Number(to.params.rpid)

                this.getOrgList()
                this.getSpaceList()

                if (to.params.oid) {
                    this.selectedOrgId = Number(to.params.oid)
                } else {
                    this.selectedOrgId = null
                }

                if (to.params.sid) {
                    this.selectedSpaceId = Number(to.params.sid)
                } else {
                    this.selectedSpaceId = null
                }
            },
            immediate: true
        }
    }
}
</script>

<style lang="scss" scoped>
.hidden {
    visibility: hidden;
}

.deleted {
    text-decoration: line-through;
    opacity: 0.5;
}
</style>
